import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CheckCircle2, Home, Calendar, Download, Mail, MapPin, Users, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface FinalConfirmedAdventure {
  requestNumber: string;
  adventureName: string;
  businessName: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  participants: {
    adults: number;
    children: number;
    students: number;
  };
  finalPrice: {
    total: number;
    currency: string;
  };
  customerDetails: {
    name: string;
    email: string;
    phone: string;
  };
  paymentCompleted: boolean;
  paymentDate: string;
}

export default function CustomAdventureFinalConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestNumber } = useParams<{ requestNumber: string }>();
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures.finalConfirmation' });
  const { t: tCommon } = useTranslation('common');
  
  const [adventureDetails, setAdventureDetails] = useState<FinalConfirmedAdventure | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const data = location.state?.adventureData;
    
    if (data) {
      setAdventureDetails(data);
    }
    
    setLoading(false);
  }, [location.state]);

  const getTotalParticipants = () => {
    if (!adventureDetails) return 0;
    return adventureDetails.participants.adults + adventureDetails.participants.children + adventureDetails.participants.students;
  };

  const handleDownloadConfirmation = () => {
    // This would generate and download a PDF confirmation
    console.log('Downloading confirmation for:', requestNumber);
    // Implementation would go here
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#77A146]" />
      </div>
    );
  }

  if (!adventureDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('notFound.title')}</h1>
          <p className="text-gray-600 mb-4">{t('notFound.description')}</p>
          <Button onClick={() => navigate('/')} className="bg-[#77A146] hover:bg-[#6a9139]">
            <Home className="h-4 w-4 mr-2" />
            {t('notFound.backHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F0] p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle2 className="h-16 w-16 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {t('title')}
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            {t('subtitle')}
          </p>
          <p className="text-lg font-semibold text-green-600">
            {t('bookingReference')}: {adventureDetails.requestNumber}
          </p>
        </div>

        {/* Confirmation Details Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-6">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Adventure Details */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-[#77A146]" />
                {t('sections.adventureDetails')}
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">{t('fields.adventure')}</p>
                  <p className="font-medium text-gray-900">{adventureDetails.adventureName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('fields.provider')}</p>
                  <p className="font-medium text-gray-900">{adventureDetails.businessName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('fields.dates')}</p>
                  <p className="font-medium text-gray-900">
                    {format(new Date(adventureDetails.dateRange.startDate), 'MMMM d, yyyy')} - {' '}
                    {format(new Date(adventureDetails.dateRange.endDate), 'MMMM d, yyyy')}
                  </p>
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
                <Users className="h-5 w-5 mr-2 text-[#77A146]" />
                {t('sections.bookingDetails')}
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">{t('fields.participants')}</p>
                  <p className="font-medium text-gray-900">{getTotalParticipants()} people</p>
                  <div className="text-sm text-gray-600">
                    {adventureDetails.participants.adults > 0 && <span>{adventureDetails.participants.adults} Adults</span>}
                    {adventureDetails.participants.children > 0 && <span>, {adventureDetails.participants.children} Children</span>}
                    {adventureDetails.participants.students > 0 && <span>, {adventureDetails.participants.students} Students</span>}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('fields.totalPaid')}</p>
                  <p className="font-medium text-green-600 text-lg">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: adventureDetails.finalPrice.currency
                    }).format(adventureDetails.finalPrice.total)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('fields.paymentDate')}</p>
                  <p className="font-medium text-gray-900">
                    {format(new Date(adventureDetails.paymentDate), 'MMMM d, yyyy')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Status */}
          <div className="flex justify-center mb-6">
            <Badge className="bg-green-100 text-green-800 px-4 py-2 text-sm">
              <CreditCard className="h-4 w-4 mr-2" />
              {t('status.paymentComplete')}
            </Badge>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
            <Mail className="h-5 w-5 mr-2" />
            {t('nextSteps.title')}
          </h3>
          <ul className="space-y-2 text-blue-800">
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">1</span>
              {t('nextSteps.step1')}
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">2</span>
              {t('nextSteps.step2')}
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">3</span>
              {t('nextSteps.step3')}
            </li>
          </ul>
        </div>

        {/* Customer Information */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">
            {t('sections.customerInfo')}
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('fields.name')}</p>
              <p className="font-medium text-gray-900">{adventureDetails.customerDetails.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('fields.email')}</p>
              <p className="font-medium text-gray-900">{adventureDetails.customerDetails.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('fields.phone')}</p>
              <p className="font-medium text-gray-900">{adventureDetails.customerDetails.phone}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={handleDownloadConfirmation}
            variant="outline"
            className="bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            {t('actions.downloadConfirmation')}
          </Button>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#77A146] hover:bg-[#6a9139]"
          >
            <Home className="h-4 w-4 mr-2" />
            {t('actions.backToHome')}
          </Button>
        </div>

        {/* Important Note */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-amber-900 mb-2">
            {t('importantNote.title')}
          </h3>
          <p className="text-amber-800">
            {t('importantNote.description')}
          </p>
        </div>
      </div>
    </div>
  );
}
